import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    ImagePlus,
    Trash2,
    Briefcase,
    Plus,
    X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fileToImageDataUrl } from "@/lib/hooks";
import { mirror } from "@/lib/syncStore";
import { SUPABASE_TABLES } from "@/lib/supabaseClient";
import {
    BUSINESS_CATEGORIES,
} from "@/lib/businessCategories";

const PORTFOLIO_MAX = 12;

/**
 * Business-only edit profile.
 * Loaded from `EditProfile.jsx` when `user.accountType === "business"`.
 *
 * Renders ONLY business fields — never personal name / DOB / personal bio /
 * interests. Personal users will never see this screen.
 */
const BusinessEditProfile = () => {
    const navigate = useNavigate();
    const { user, updateUser, usernameIsTaken } = useAuth();
    const logoRef = useRef(null);
    const bannerRef = useRef(null);
    const portfolioRef = useRef(null);

    const [logo, setLogo]               = useState(user?.businessLogo || user?.avatar || null);
    const [banner, setBanner]           = useState(user?.banner || null);
    const [businessName, setBusinessName]
        = useState(user?.businessName || user?.fullName || "");
    const [username, setUsername]       = useState(user?.username || "");
    const [category, setCategory]       = useState(user?.businessCategory || "");
    const [categoryCustom, setCategoryCustom] = useState(user?.businessCategoryCustom || "");
    const [description, setDescription] = useState(user?.businessDescription || "");
    const [address, setAddress]         = useState(user?.businessAddress || "");
    const [location, setLocation]       = useState(user?.location || "");
    const [phone, setPhone]             = useState(user?.businessPhone || user?.phone || "");
    const [startingPrice, setStartingPrice]
        = useState(user?.startingPrice ? String(user.startingPrice) : "");
    const [portfolio, setPortfolio]
        = useState(Array.isArray(user?.portfolio) ? user.portfolio.slice(0, PORTFOLIO_MAX) : []);
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);

    if (!user) return null;
    if (user.accountType !== "business") {
        // Defensive — only business accounts see this screen.
        navigate("/profile/edit", { replace: true });
        return null;
    }

    const onPickLogo = async (file) => {
        if (!file?.type?.startsWith("image/")) return;
        setBusy(true);
        try {
            setLogo(await fileToImageDataUrl(file, 800, 0.85));
        } finally { setBusy(false); }
    };
    const onPickBanner = async (file) => {
        if (!file?.type?.startsWith("image/")) return;
        setBusy(true);
        try {
            setBanner(await fileToImageDataUrl(file, 1600, 0.82));
        } finally { setBusy(false); }
    };
    const onPickPortfolio = async (files) => {
        if (!files?.length) return;
        setBusy(true);
        const next = [...portfolio];
        for (const f of files) {
            if (next.length >= PORTFOLIO_MAX) break;
            if (!f.type?.startsWith("image/")) continue;
            next.push(await fileToImageDataUrl(f, 1400, 0.82));
        }
        setPortfolio(next);
        setBusy(false);
    };
    const removePortfolio = (i) =>
        setPortfolio((arr) => arr.filter((_, idx) => idx !== i));

    const submit = () => {
        const e = {};
        if (!businessName.trim() || businessName.trim().length < 2)
            e.businessName = "Business name is required";
        const u = username.trim();
        if (!u) e.username = "Username is required";
        else if (!/^[a-zA-Z0-9_.]{3,20}$/.test(u))
            e.username = "3–20 chars, letters, numbers, _ or .";
        else if (u !== user.username && usernameIsTaken(u))
            e.username = "Username already taken";
        if (!category) e.category = "Pick a category";
        else if (category === "Other (specify)" && !categoryCustom.trim())
            e.categoryCustom = "Specify your business category";
        if (!address.trim()) e.address = "Business address is required";
        if (!location.trim()) e.location = "City / location is required";
        if (startingPrice && Number.isNaN(Number(startingPrice)))
            e.startingPrice = "Enter a valid number";

        if (Object.keys(e).length) { setErrors(e); return; }

        const patch = {
            businessName: businessName.trim(),
            username: u,
            businessCategory: category,
            businessCategoryCustom:
                category === "Other (specify)" ? categoryCustom.trim() : null,
            businessAddress: address.trim(),
            businessPhone: phone.trim() || null,
            businessDescription: description.trim() || null,
            businessLogo: logo || null,
            avatar: logo || null,
            banner: banner || null,
            location: location.trim(),
            portfolio,
            startingPrice: startingPrice ? Number(startingPrice) : null,
        };
        updateUser(patch);

        try {
            mirror(SUPABASE_TABLES.BUSINESS_PROFILES, {
                id: user.id,
                userId: user.id,
                ...patch,
                updatedAt: new Date().toISOString(),
            });
        } catch {}

        navigate("/profile", { replace: true });
    };

    return (
        <div className="screen relative" data-testid="business-edit-profile-screen">
            <header className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-border/60 flex items-center justify-between">
                <button
                    type="button"
                    data-testid="biz-edit-back"
                    onClick={() => navigate(-1)}
                    className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted"
                >
                    <ArrowLeft size={20} />
                </button>
                <h1 className="font-display text-[16px] font-semibold">Edit business profile</h1>
                <span className="h-10 w-10" />
            </header>

            <div className="flex-1 overflow-y-auto no-scrollbar pb-32">
                {/* Banner */}
                <div className="relative h-40 bg-secondary border-b border-border" data-testid="biz-edit-banner-wrap">
                    {banner ? (
                        <img src={banner} alt="" className="h-full w-full object-cover" />
                    ) : (
                        <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                            <ImagePlus size={20} />
                            <span className="ml-2 text-[12px] font-medium">Add a banner image</span>
                        </div>
                    )}
                    <button
                        type="button"
                        data-testid="biz-edit-banner-pick"
                        onClick={() => bannerRef.current?.click()}
                        className="absolute bottom-3 right-3 h-9 px-3 rounded-full bg-background/95 border border-border text-[12px] font-medium inline-flex items-center gap-1.5 active:scale-[0.98]"
                    >
                        <ImagePlus size={13} />
                        {banner ? "Replace" : "Upload"}
                    </button>
                    <input
                        ref={bannerRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="biz-edit-banner-input"
                        onChange={(e) => onPickBanner(e.target.files?.[0])}
                    />
                </div>

                {/* Logo */}
                <div className="px-5 -mt-8 mb-5 flex items-end gap-3">
                    <div className="relative">
                        <div className="h-20 w-20 rounded-2xl overflow-hidden border-4 border-background bg-secondary flex items-center justify-center shadow-card">
                            {logo ? (
                                <img src={logo} alt="" className="h-full w-full object-cover" />
                            ) : (
                                <Briefcase size={22} className="text-muted-foreground" />
                            )}
                        </div>
                        <button
                            type="button"
                            data-testid="biz-edit-logo-pick"
                            onClick={() => logoRef.current?.click()}
                            className="absolute -bottom-1.5 -right-1.5 h-7 w-7 rounded-full bg-primary text-primary-foreground border-2 border-background flex items-center justify-center"
                            aria-label="Upload logo"
                        >
                            <ImagePlus size={12} />
                        </button>
                        <input
                            ref={logoRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            data-testid="biz-edit-logo-input"
                            onChange={(e) => onPickLogo(e.target.files?.[0])}
                        />
                    </div>
                    <div className="flex-1 pb-1">
                        <p className="text-[11.5px] text-muted-foreground">
                            Upload a clear logo on a plain background — square works best.
                        </p>
                    </div>
                </div>

                <div className="px-5 space-y-4">
                    <Field label="Business name" error={errors.businessName} testId="biz-edit-name">
                        <input
                            data-testid="biz-edit-name"
                            value={businessName}
                            onChange={(e) => { setBusinessName(e.target.value); if (errors.businessName) setErrors({ ...errors, businessName: null }); }}
                            placeholder="e.g. Glow Studio"
                            className="flex-1 bg-transparent outline-none text-[15px]"
                        />
                    </Field>

                    <Field label="Username" error={errors.username} testId="biz-edit-username">
                        <span className="text-muted-foreground">@</span>
                        <input
                            data-testid="biz-edit-username"
                            value={username}
                            onChange={(e) => { setUsername(e.target.value.replace(/\s+/g, "")); if (errors.username) setErrors({ ...errors, username: null }); }}
                            maxLength={20}
                            placeholder="glow_studio"
                            className="flex-1 bg-transparent outline-none text-[15px]"
                        />
                    </Field>

                    <div data-testid="biz-edit-category-field">
                        <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                            Business category
                        </span>
                        <select
                            data-testid="biz-edit-category-select"
                            value={category}
                            onChange={(e) => { setCategory(e.target.value); if (errors.category) setErrors({ ...errors, category: null }); }}
                            className={[
                                "w-full h-14 px-4 rounded-2xl border text-[15px] bg-secondary outline-none transition-colors appearance-none",
                                errors.category
                                    ? "border-destructive bg-destructive/5"
                                    : "border-border focus:bg-background focus:border-primary",
                            ].join(" ")}
                        >
                            <option value="">Select a category…</option>
                            {BUSINESS_CATEGORIES.map((c) => (
                                <option key={c.id} value={c.label}>{c.label}</option>
                            ))}
                        </select>
                        {errors.category && (
                            <p data-testid="biz-edit-category-error" className="text-[12px] text-destructive mt-1.5 ml-1">
                                {errors.category}
                            </p>
                        )}
                        {category === "Other (specify)" && (
                            <div className="mt-2" data-testid="biz-edit-category-custom-field">
                                <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                                    Specify your business category
                                </span>
                                <input
                                    data-testid="biz-edit-category-custom"
                                    value={categoryCustom}
                                    onChange={(e) => { setCategoryCustom(e.target.value); if (errors.categoryCustom) setErrors({ ...errors, categoryCustom: null }); }}
                                    placeholder="Enter your category"
                                    className={[
                                        "w-full h-12 px-3 rounded-xl border text-[14px] bg-secondary outline-none transition-colors",
                                        errors.categoryCustom
                                            ? "border-destructive bg-destructive/5"
                                            : "border-border focus:bg-background focus:border-primary",
                                    ].join(" ")}
                                />
                                {errors.categoryCustom && (
                                    <p data-testid="biz-edit-category-custom-error" className="text-[12px] text-destructive mt-1.5 ml-1">
                                        {errors.categoryCustom}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    <div data-testid="biz-edit-description-field">
                        <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                            Business description
                        </span>
                        <textarea
                            data-testid="biz-edit-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={4}
                            maxLength={500}
                            placeholder="What does your business do? What makes it special?"
                            className="w-full p-3.5 rounded-2xl border border-border bg-secondary outline-none text-[14px] resize-none focus:bg-background focus:border-primary transition-colors"
                        />
                    </div>

                    <Field label="Business address" error={errors.address} testId="biz-edit-address">
                        <input
                            data-testid="biz-edit-address"
                            value={address}
                            onChange={(e) => { setAddress(e.target.value); if (errors.address) setErrors({ ...errors, address: null }); }}
                            placeholder="Street, building, area"
                            className="flex-1 bg-transparent outline-none text-[15px]"
                        />
                    </Field>

                    <Field label="City / Location" error={errors.location} testId="biz-edit-location">
                        <input
                            data-testid="biz-edit-location"
                            value={location}
                            onChange={(e) => { setLocation(e.target.value); if (errors.location) setErrors({ ...errors, location: null }); }}
                            placeholder="e.g. Lagos, Nigeria"
                            className="flex-1 bg-transparent outline-none text-[15px]"
                        />
                    </Field>

                    <Field label="Business phone" testId="biz-edit-phone">
                        <input
                            data-testid="biz-edit-phone"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            inputMode="tel"
                            placeholder="+234…"
                            className="flex-1 bg-transparent outline-none text-[15px]"
                        />
                    </Field>

                    <Field label="Starting price (₦)" error={errors.startingPrice} testId="biz-edit-starting-price">
                        <span className="text-muted-foreground">₦</span>
                        <input
                            data-testid="biz-edit-starting-price"
                            value={startingPrice}
                            onChange={(e) => { setStartingPrice(e.target.value.replace(/[^0-9.]/g, "")); if (errors.startingPrice) setErrors({ ...errors, startingPrice: null }); }}
                            inputMode="decimal"
                            placeholder="e.g. 25000"
                            className="flex-1 bg-transparent outline-none text-[15px] tabular-nums"
                        />
                    </Field>

                    {/* Portfolio */}
                    <section data-testid="biz-edit-portfolio">
                        <div className="flex items-center justify-between mb-1.5 ml-1">
                            <span className="text-[12px] font-medium text-muted-foreground">
                                Portfolio · {portfolio.length}/{PORTFOLIO_MAX}
                            </span>
                            <button
                                type="button"
                                data-testid="biz-edit-portfolio-add"
                                onClick={() => portfolioRef.current?.click()}
                                disabled={portfolio.length >= PORTFOLIO_MAX || busy}
                                className="text-[12px] font-medium text-primary inline-flex items-center gap-1 disabled:opacity-50"
                            >
                                <Plus size={12} />
                                Add image
                            </button>
                            <input
                                ref={portfolioRef}
                                type="file"
                                accept="image/*"
                                multiple
                                className="hidden"
                                data-testid="biz-edit-portfolio-input"
                                onChange={(e) => onPickPortfolio(e.target.files)}
                            />
                        </div>
                        {portfolio.length === 0 ? (
                            <button
                                type="button"
                                data-testid="biz-edit-portfolio-empty"
                                onClick={() => portfolioRef.current?.click()}
                                className="w-full aspect-[16/8] rounded-2xl border-2 border-dashed border-border bg-secondary text-muted-foreground text-[12.5px] font-medium hover:border-primary hover:text-primary transition-colors flex flex-col items-center justify-center gap-1.5"
                            >
                                <ImagePlus size={20} />
                                Showcase your best work — up to {PORTFOLIO_MAX} images
                            </button>
                        ) : (
                            <div className="grid grid-cols-3 gap-2">
                                {portfolio.map((src, i) => (
                                    <div
                                        key={i}
                                        className="relative aspect-square rounded-xl overflow-hidden border border-border"
                                        data-testid={`biz-edit-portfolio-item-${i}`}
                                    >
                                        <img src={src} alt="" className="h-full w-full object-cover" />
                                        <button
                                            type="button"
                                            data-testid={`biz-edit-portfolio-remove-${i}`}
                                            onClick={() => removePortfolio(i)}
                                            className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/95 border border-border flex items-center justify-center text-destructive"
                                        >
                                            <X size={11} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </div>
            </div>

            <div className="absolute bottom-0 inset-x-0 px-5 pt-3 pb-[max(20px,env(safe-area-inset-bottom))] border-t border-border/60 bg-background/95 backdrop-blur">
                <button
                    data-testid="biz-edit-save"
                    onClick={submit}
                    disabled={busy}
                    className="w-full h-14 rounded-2xl text-primary-foreground font-medium text-[15px] active:scale-[0.98] disabled:opacity-60"
                    style={{
                        background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                        boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                    }}
                >
                    Save business profile
                </button>
            </div>
        </div>
    );
};

const Field = ({ label, error, testId, children }) => (
    <label className="block" data-testid={`${testId}-field`}>
        <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">{label}</span>
        <span className={[
            "flex items-center gap-2 h-14 px-4 rounded-2xl border transition-colors",
            error
                ? "border-destructive bg-destructive/5"
                : "border-border bg-secondary focus-within:bg-background focus-within:border-primary",
        ].join(" ")}>
            {children}
        </span>
        {error && <span className="block text-[12px] text-destructive mt-1.5 ml-1" data-testid={`${testId}-error`}>{error}</span>}
    </label>
);

// Avoid the unused-import lint warning — `Trash2` is reserved for a future
// "remove banner" affordance and kept intentionally.
void Trash2;

export default BusinessEditProfile;
